/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as uuidv4 } from 'uuid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Node, Edge } from 'reactflow';
import { getServicesThunk, Service } from '@store/async/service';
import { Flow } from '@store/async/flow';
import { NodeConfig, NodeField, NodeOutput, NodeState } from '@components/Flow/Nodes';
import { FullEdge, Translation, TranslationNode } from '@components/Flow';
import { TemplatesCreateArgs } from '@requests/templates';
import { GlobalVariableBaseValues } from '@components/Workspace/routes/GlobalVariables/types';
import { ModuleWorkflowsAssigned } from '@components/Workspace/routes/Workflows';
import { AITool } from '@components/Workspace/routes/Assistants/Assistant/AssistantTools/tool.types';

const parseCredential = (input: string | null): CallCredential | null => {
  try {
    if (!input) return null;
    return JSON.parse(input) as CallCredential;
  } catch (e) {
    return null;
  }
};

const storedTheme = localStorage.getItem('theme') ? (localStorage.getItem('theme') as 'light' | 'dark') : null;
const storedCredential = localStorage.getItem('callCredential')
  ? parseCredential(localStorage.getItem('callCredential'))
  : null;

export type Command = {
  node: Node;
  connections: FullEdge[];
  fields: NodeField[];
  config: NodeConfig;
  state: NodeState;
  output: NodeOutput;
};

export type NodeData = {
  id: string;
  config?: NodeConfig;
  fields?: NodeField[];
  state?: NodeState;
};

export type NodeAuthProvider = {
  OPENAI_API_KEY: string | null;
  GOOGLE_API_KEY: string | null;
  ANTHROPIC_API_KEY: string | null;
  GROK_API_KEY: string | null;
  GROQ_API_KEY: string | null;
};

export type StateUpdate = {
  id: string;
  field: string;
  field_id?: string;
  value: any;
  node_id: string;
  index?: string;
  index_field?: string;
};

export enum FlowModals {
  SelectNewFlow = 'SelectNewFlow',
  SelectTemplate = 'SelectTemplate',
  SaveAsTemplate = 'SaveAsTemplate',
  EditFlow = 'EditFlow',
}

export type FlowCreationVariant = 'workflow' | 'module';

export type FlowCreationModal = {
  type: FlowModals.SelectNewFlow | FlowModals.SelectTemplate;
  variant: FlowCreationVariant;
};

export type FlowEditModal = {
  type: FlowModals.EditFlow;
};

export type FlowSaveAsTemplateModal = {
  type: FlowModals.SaveAsTemplate;
  data: Omit<TemplatesCreateArgs, 'description'>;
};

export type FlowModal = FlowCreationModal | FlowEditModal | FlowSaveAsTemplateModal;

export enum FlowGlobalVariableModalVariant {
  READ = 'READ',
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

type FlowGlobalVariableReadModal = {
  type: FlowGlobalVariableModalVariant.READ;
  data: GlobalVariableBaseValues & { id: string };
};

type FlowGlobalVariableCreateModal = {
  type: FlowGlobalVariableModalVariant.CREATE;
};

type FlowGlobalVariableEditModal = {
  type: FlowGlobalVariableModalVariant.EDIT;
  data: GlobalVariableBaseValues & { id: string };
};

export type FlowGlobalVariableDeleteModal = {
  type: FlowGlobalVariableModalVariant.DELETE;
  data: { id: string; key: string };
};

type FlowGlobalVariableModal =
  | FlowGlobalVariableReadModal
  | FlowGlobalVariableCreateModal
  | FlowGlobalVariableEditModal
  | FlowGlobalVariableDeleteModal;

export type CallCredential = {
  id: string;
  username: string;
  password: string;
};

export type DeleteAssistantTool = { assistantId: string; tool: AITool; type: string };

export type flowTypes = {
  id: string | null;
  name: string;
  saved: boolean;
  commands: Command[];
  nodes: Node[];
  edges: Edge[];
  nodeIndex: number;
  edgeIndex: number;
  editBucketFiles: string | null;
  beginnerRightClickInfo: boolean;
  selectedNode: string | null;
  triggerSelection: boolean;
  flowDuplicate: boolean;
  createService: boolean;
  createSecret: boolean;
  deletePermissionGroup: string | null;
  deleteUserRole: string | null;
  deleteSecret: string | null;
  createBucket: boolean;
  syncBuckets: boolean;
  viewModuleWorkflows: ModuleWorkflowsAssigned[] | null;
  closeTab: string | null;
  deleteTab: string | null;
  deleteService: string | null;
  deleteBucket: string | null;
  clearBucket: string | null;
  deleteAssistant: string | null;
  deleteAssistantTool: DeleteAssistantTool | null;
  assignWorkspaceAdmin: string | null;
  removeWorkspaceAdmin: string | null;
  modal: FlowModal | null;
  globalVariableModal: FlowGlobalVariableModal | null;
  editingName: boolean;
  flowError: string | null;
  flowLoading: boolean;
  flows: Flow[];
  loading: boolean;
  theme: 'light' | 'dark';
  services: Service[];
  endpoint: string | null;
  showUnsaved: string | null;
  showUnsavedType: 'link' | 'path' | null;
  showDataPrefix: string | null;
  path: string | null;
  viewExecuteInfo: boolean;
  nodeTestRun: TranslationNode | null;
  workflowTestRun: Translation | null;
  workflowMessageRun: Translation | null;
  connecting: boolean;
  test_execution: string | null;
  session_id: string;
  callCredential: CallCredential | null;
  showNodeChangelog: NodeConfig | null;
  returnPath: string | null;
};

const initialState: flowTypes = {
  id: uuidv4(),
  saved: false,
  theme: storedTheme || 'light',
  name: '',
  nodes: [],
  commands: [],
  edges: [],
  triggerSelection: false,
  createService: false,
  createBucket: false,
  syncBuckets: false,
  viewModuleWorkflows: null,
  closeTab: null,
  deleteTab: null,
  deleteBucket: null,
  clearBucket: null,
  deleteAssistant: null,
  deleteAssistantTool: null,
  beginnerRightClickInfo: false,
  deleteSecret: null,
  deletePermissionGroup: null,
  deleteUserRole: null,
  assignWorkspaceAdmin: null,
  removeWorkspaceAdmin: null,
  createSecret: false,
  modal: null,
  globalVariableModal: null,
  showUnsaved: null,
  showUnsavedType: null,
  showDataPrefix: null,
  deleteService: null,
  editBucketFiles: null,
  nodeIndex: 1,
  nodeTestRun: null,
  workflowTestRun: null,
  workflowMessageRun: null,
  viewExecuteInfo: false,
  edgeIndex: 0,
  selectedNode: null,
  editingName: false,
  flowError: null,
  flowLoading: false,
  loading: false,
  flows: [],
  flowDuplicate: false,
  services: [],
  endpoint: null,
  path: null,
  connecting: false,
  test_execution: null,
  session_id: uuidv4(),
  callCredential: storedCredential,
  showNodeChangelog: null,
  returnPath: null,
};

const flowSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.edges = [];
      state.commands = [];
      state.name = '';
      state.nodeIndex = 1;
      state.edgeIndex = 0;
      state.selectedNode = null;
      state.flowError = null;
      state.editingName = false;
      state.session_id = uuidv4();
      state.path = null;
      state.loading = false;
      state.flowLoading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIndexes: (state, action: PayloadAction<{ nodeIndex: number; edgeIndex: number }>) => {
      state.nodeIndex = action.payload.nodeIndex;
      state.edgeIndex = action.payload.edgeIndex;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setCallCredential: (state, action: PayloadAction<CallCredential | null>) => {
      state.callCredential = action.payload;
    },
    setEditingName: (state, action: PayloadAction<boolean>) => {
      state.editingName = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;

      localStorage.setItem('theme', action.payload);
    },
    setTriggerSelection: (state, action: PayloadAction<boolean>) => {
      state.triggerSelection = action.payload;
    },

    setNodes: (state, action: PayloadAction<Node[]>) => {
      state.nodes = action.payload;
    },
    setEdges: (state, action: PayloadAction<Edge[]>) => {
      state.edges = action.payload;
    },
    setDeleteSecret: (state, action: PayloadAction<string | null>) => {
      state.deleteSecret = action.payload;
    },
    setDeletePermissionGroup: (state, action: PayloadAction<string | null>) => {
      state.deletePermissionGroup = action.payload;
    },
    setDeleteUserRole: (state, action: PayloadAction<string | null>) => {
      state.deleteUserRole = action.payload;
    },
    setCreateSecret: (state, action: PayloadAction<boolean>) => {
      state.createSecret = action.payload;
    },
    setFlowGlobalVariableModal: (state, action: PayloadAction<FlowGlobalVariableModal | null>) => {
      state.globalVariableModal = action.payload;
    },
    setShowNodeChangelog: (state, action: PayloadAction<NodeConfig | null>) => {
      state.showNodeChangelog = action.payload;
    },
    setAssignWorkspaceAdmin: (state, action: PayloadAction<string | null>) => {
      state.removeWorkspaceAdmin = null;
      state.assignWorkspaceAdmin = action.payload;
    },
    setRemoveWorkspaceAdmin: (state, action: PayloadAction<string | null>) => {
      state.assignWorkspaceAdmin = null;
      state.removeWorkspaceAdmin = action.payload;
    },
    setViewExecuteInfo: (state, action: PayloadAction<boolean>) => {
      state.viewExecuteInfo = action.payload;
    },
    setNodeTestRun: (state, action: PayloadAction<TranslationNode | null>) => {
      state.nodeTestRun = action.payload;
    },
    setWorkflowTestRun: (state, action: PayloadAction<Translation | null>) => {
      state.workflowTestRun = action.payload;
    },
    setWorkflowMessageRun: (state, action: PayloadAction<Translation | null>) => {
      state.workflowMessageRun = action.payload;
    },
    setShowUnsaved: (state, action: PayloadAction<string | null>) => {
      state.showUnsaved = action.payload;
    },
    setShowUnsavedType: (state, action: PayloadAction<'link' | 'path'>) => {
      state.showUnsavedType = action.payload;
    },
    setShowDataPrefix: (state, action: PayloadAction<string | null>) => {
      state.showDataPrefix = action.payload;
    },
    setCreateService: (state, action: PayloadAction<boolean>) => {
      state.createService = action.payload;
    },
    setCreateBucket: (state, action: PayloadAction<boolean>) => {
      state.createBucket = action.payload;
    },
    setFlowModal: (state, action: PayloadAction<FlowModal | null>) => {
      state.modal = action.payload;
    },
    setSyncBuckets: (state, action: PayloadAction<boolean>) => {
      state.syncBuckets = action.payload;
    },
    setDeleteBucket: (state, action: PayloadAction<string | null>) => {
      state.deleteBucket = action.payload;
    },
    setEditBucketFiles: (state, action: PayloadAction<string | null>) => {
      state.editBucketFiles = action.payload;
    },
    setDeleteAssistant: (state, action: PayloadAction<string | null>) => {
      state.deleteAssistant = action.payload;
    },
    setDeleteAssistantTool: (state, action: PayloadAction<DeleteAssistantTool | null>) => {
      state.deleteAssistantTool = action.payload;
    },
    setClearBucket: (state, action: PayloadAction<string | null>) => {
      state.clearBucket = action.payload;
    },
    setDeleteService: (state, action: PayloadAction<string | null>) => {
      state.deleteService = action.payload;
    },
    setCloseTab: (state, action: PayloadAction<string | null>) => {
      state.closeTab = action.payload;
    },
    setViewModuleWorkflows: (state, action: PayloadAction<ModuleWorkflowsAssigned[] | null>) => {
      state.viewModuleWorkflows = action.payload;
    },
    setDeleteTab: (state, action: PayloadAction<{ id: string | null; returnPath?: string }>) => {
      state.deleteTab = action.payload.id;
      if (action.payload.returnPath) {
        state.returnPath = action.payload.returnPath;
      }
    },
    setPath: (state, action: PayloadAction<string | null>) => {
      state.path = action.payload;
    },
    setConnecting: (state, action: PayloadAction<boolean>) => {
      state.connecting = action.payload;
    },
    setTestExecution: (state, action: PayloadAction<string | null>) => {
      state.test_execution = action.payload;
    },
    setBeginnerRightClickInfo: (state, action: PayloadAction<boolean>) => {
      state.beginnerRightClickInfo = action.payload;
    },
    refreshSessionId: (state) => {
      state.session_id = uuidv4();
    },
    setEndpoint: (state, action: PayloadAction<string | null>) => {
      state.endpoint = action.payload;

      state.commands = state.commands.map((command: Command) => {
        const states = Object.keys(command.state);
        const endpoints = ['http_get_endpoint', 'http_post_endpoint', 'http_webhook_endpoint'];

        if (states.some((state) => endpoints.includes(state))) {
          return {
            ...command,
            state: {
              ...command.state,
              http_get_endpoint: action.payload,
              http_post_endpoint: action.payload,
              http_webhook_endpoint: action.payload,
            },
          };
        }

        return command;
      });
    },
  },
  extraReducers: (builder) => {
    const serviceReducer = getServicesThunk(builder);

    return {
      serviceReducer,
    };
  },
});

export const flowActions = flowSlice.actions;
export default flowSlice.reducer;
